import PropTypes from 'prop-types'
import { Button, message, Switch, Table } from 'antd'
import { FileExcelOutlined, FileSearchOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useMemo } from 'react'
import queryString from 'query-string'
import get from 'lodash/get'
import { useDispatch } from 'react-redux'
import { createUseStyles, useTheme } from 'react-jss'
import { useParams, useLocation } from 'react-router-dom'
import { useIsAdmin } from '../../../redux/slices/session'
import { useAsync, useIsMobile } from '../../../hooks'
import { PAGE_SIZE_OPTIONS } from '../../../lib/constants'
import {
  updateOfficeAuthorization,
  useFetchOfficeAuthorizations,
  useOfficeAuthorizations,
  useOfficeAuthorizationsPagination,
} from '../../../redux/slices/office-authorizations'
import { ListError } from '../../Common'
import { toPath } from '../../../routes'
import { formatDOB } from '../../../lib/datetime'
import useExportExcel from '../../../hooks/useExportExcel'
import DownloadLink from '../../Common/DownloadLink'
import CircleCheckIcon from '../../Common/CircleCheckIcon'
import PatientEntry from '../../Common/PatientEntry'
import {
  EXPAND_COLUMN_WIDTH,
  OfficeAuthorizationMobileCard,
  OfficeAuthorizationMobileDetails,
  SELECTION_COLUMN_WIDTH,
} from './OfficeAuthorizationMobileRow'
import ButtonLink from '../../Common/ButtonLink'

const useStyles = createUseStyles({
  inactiveRow: {
    '& td': {
      color: '#999 !important',
      fontStyle: 'italic',
    },
    opacity: 0.6,
  },
})

const BASE_COLUMNS = [
  {
    title: 'Patient',
    dataIndex: ['patient', 'lastName'],
    render(_, preAuthorization) {
      return <PatientEntry patient={preAuthorization.patient} />
    },
  },
  {
    title: 'MRN',
    key: 'MRN',
    dataIndex: ['patient', 'practiceMrn'],
  },
  {
    title: 'DOB',
    key: 'dob',
    dataIndex: ['patient', 'birthDate'],
    render: formatDOB,
  },
  {
    title: 'Device Type',
    dataIndex: ['device', 'deviceModel', 'deviceType', 'name'],
    key: 'deviceType',
  },
  {
    title: 'CPT Code',
    dataIndex: 'cptCode',
    key: 'cptCode',
  },
  {
    title: 'ICD-10 Code',
    dataIndex: 'icd10Code',
    key: 'icd10Code',
  },
  {
    title: 'Billing Date',
    dataIndex: 'billingDate',
    key: 'billingDate',
  },
  {
    title: 'Auth #',
    key: 'auth',
    render(_text, preAuthorization) {
      const { insuranceAuthorizationFilePath, insuranceAuthorizationNumber } =
        preAuthorization
      if (!insuranceAuthorizationFilePath) return insuranceAuthorizationNumber

      return (
        <DownloadLink path={insuranceAuthorizationFilePath}>
          <span>{insuranceAuthorizationNumber}</span>
        </DownloadLink>
      )
    },
  },
]

// Hoisted out of the component so the expanded row is a stable CallExpression
// (an inline arrow here trips react/no-unstable-nested-components).
const renderMobileDetails = indent => officeAuthorization => (
  <OfficeAuthorizationMobileDetails
    officeAuthorization={officeAuthorization}
    indent={indent}
  />
)

export default function OfficeAuthorizations({
  checkDate,
  onPaginationChange,
  page = 1,
  perPage = 50,
  filters = {},
  rowSelection = null,
}) {
  const { practiceId } = useParams()
  const isAdmin = useIsAdmin()
  const isMobile = useIsMobile()
  const officeAuthorizations = useOfficeAuthorizations(checkDate)
  const { total } = useOfficeAuthorizationsPagination(checkDate)
  const { pending, error, clearError, execute } = useAsync(
    useFetchOfficeAuthorizations(practiceId, page, filters, perPage),
    false,
  )
  const dispatch = useDispatch()
  const classes = useStyles()
  const { brandWarning } = useTheme()
  const location = useLocation()

  useEffect(() => {
    sessionStorage.setItem(
      'patientReturnTo',
      `${location.pathname}${location.search}`,
    )
  }, [location.pathname, location.search])

  useEffect(() => {
    execute(practiceId, page, filters, perPage)
  }, [execute, practiceId, page, filters, perPage])

  const handleAuthorizedChange = useCallback(
    async (id, authorized) => {
      try {
        await dispatch(updateOfficeAuthorization(id, { authorized }))
        message.success('Remote authorization successfully updated')
      } catch (e) {
        message.error(
          get(
            e,
            'response.errors.authorized',
            'Oops, we had a problem. Please try submitting again',
          ),
        )
      }
    },
    [dispatch],
  )

  const columns = useMemo(() => {
    const filesColumns = [
      {
        title: 'PDF',
        dataIndex: 'hasReportFile',
        key: 'hasReportFile',
        align: 'center',
        render(text, { hasReportFile }) {
          return hasReportFile === null ? (
            <span>-</span>
          ) : (
            <CircleCheckIcon
              checked={hasReportFile}
              falseStyle={{ color: brandWarning }}
            />
          )
        },
      },
      {
        title: 'Full',
        dataIndex: 'hasReportRawFile',
        key: 'hasReportRawFile',
        align: 'center',
        render(text, { hasReportRawFile }) {
          return hasReportRawFile === null ? (
            <span>-</span>
          ) : (
            <CircleCheckIcon
              checked={hasReportRawFile}
              falseStyle={{ color: brandWarning }}
            />
          )
        },
      },
    ]

    return [
      ...BASE_COLUMNS,
      {
        title: 'Authorized',
        key: 'authorized',
        render: (text, record) => (
          <Switch
            checked={record.authorized}
            disabled={record.active === false || record.patient?.active === false}
            checkedChildren="Authorized"
            unCheckedChildren="Not&nbsp;Authorized"
            onChange={authorized =>
              handleAuthorizedChange(record.id, authorized)
            }
          />
        ),
      },
      ...(isAdmin ? filesColumns : []),
      {
        title: 'Actions',
        key: 'actions',
        render(text, preAuthorization) {
          return (
            <ButtonLink
              to={`${toPath.billing.officeAuthorizations.officeAuthorization({
                id: preAuthorization.id,
              })}?practiceId=${practiceId}`}
              type="link"
              icon={<FileSearchOutlined />}
              aria-label="View authorization"
            />
          )
        },
      },
    ]
  }, [brandWarning, handleAuthorizedChange, isAdmin, practiceId])

  // On mobile the 12-column table collapses to one card per authorization: the
  // fields needed to triage stay visible and the rest sit behind the toggle.
  const mobileColumns = useMemo(
    () => [
      {
        title: 'Office authorization',
        key: 'officeAuthorization',
        render(_, officeAuthorization) {
          return (
            <OfficeAuthorizationMobileCard
              officeAuthorization={officeAuthorization}
              practiceId={practiceId}
              isAdmin={isAdmin}
              warningColor={brandWarning}
              onAuthorizedChange={handleAuthorizedChange}
            />
          )
        },
      },
    ],
    [brandWarning, handleAuthorizedChange, isAdmin, practiceId],
  )

  // antd spans the expanded row across the gutter columns too, so indent the
  // details by the width of every gutter this table renders.
  const detailsIndent =
    EXPAND_COLUMN_WIDTH + (rowSelection ? SELECTION_COLUMN_WIDTH : 0)

  const mobileExpandable = useMemo(
    () => ({
      columnWidth: EXPAND_COLUMN_WIDTH,
      expandedRowRender: renderMobileDetails(detailsIndent),
    }),
    [detailsIndent],
  )

  const mobileRowSelection = useMemo(
    () =>
      rowSelection && { ...rowSelection, columnWidth: SELECTION_COLUMN_WIDTH },
    [rowSelection],
  )

  const rowClassName = useCallback(
    preAuth =>
      preAuth.active === false || preAuth.patient?.active === false
        ? classes.inactiveRow
        : undefined,
    [classes.inactiveRow],
  )

  const exportUrl = useMemo(
    () =>
      queryString.stringifyUrl(
        {
          url: `/practices/${practiceId}/in-office-authorizations.xlsx`,
          query: {
            check_date: checkDate,
            q: filters?.q,
            medical_provider_id: filters?.medicalProviderId,
            office_location_id: filters?.officeLocationId,
          },
        },
        { skipNull: true, skipEmptyString: true },
      ),
    [practiceId, checkDate, filters],
  )

  const [exportingExcel, exportExcel] = useExportExcel(exportUrl)

  return (
    <>
      {error && <ListError description={error.message} onClose={clearError} />}
      <Table
        size={isMobile ? 'small' : 'middle'}
        columns={isMobile ? mobileColumns : columns}
        showHeader={!isMobile}
        expandable={isMobile ? mobileExpandable : undefined}
        dataSource={officeAuthorizations}
        rowKey="id"
        loading={pending}
        rowSelection={isMobile ? mobileRowSelection : rowSelection}
        rowClassName={rowClassName}
        pagination={{
          current: page,
          pageSize: Number(perPage),
          onChange: onPaginationChange,
          total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />
      {Boolean(officeAuthorizations.length) && (
        <div>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </>
  )
}

OfficeAuthorizations.propTypes = {
  checkDate: PropTypes.string.isRequired,
  onPaginationChange: PropTypes.func.isRequired,
  rowSelection: PropTypes.shape({}),
  page: PropTypes.number,
  perPage: PropTypes.number,
  filters: PropTypes.shape({ q: PropTypes.string }),
}
